// FET-2766: checkout "skin" (page/brand-level chrome) resolution.
//
// Replaces the hand-maintained `customSkin` array that used to live inline in
// the CMS page header_scripts. The skin controls page-level presentation only
// (logo, guarantee/social-proof imagery, before/after banner carousel, input
// styling, countdown-timer + legal links) -- it is NOT per-offer product data
// (that rides on the cart payload as `checkout_display`, see product-display.js).
//
// Sources, lowest to highest precedence:
//   1. Baked brand defaults (this module) -- the static vshred/sculptnation chrome.
//   2. Page-option overrides (the few values that legitimately vary per page,
//      e.g. the countdown timer), passed in from a `data-checkout-skin` attribute.
//   3. An optional inline CMS `customSkin` object -- retained only as a
//      zero-risk / split-test escape hatch; the per-product `product[]` hero
//      overrides it used to carry are intentionally dropped.

const isVshred = () =>
	typeof window !== 'undefined' && /vshred\.com/i.test(window.location?.href || '');

/**
 * Static, brand-aware default skin. Mirrors the values that previously lived in
 * the CMS `customSkin` constant, minus the per-product hero `product[]` array.
 */
export const buildDefaultSkin = () => {
	const vshred = isVshred();

	return {
		settings: {
			countdown_timer: { enabled: false, sticky: true, minutes: 10, seconds: 0 },
			terms_url: vshred
				? 'https://vshred.com/terms-and-conditions/'
				: 'https://sculptnation.com/terms-and-conditions',
			privacy_url: vshred
				? 'https://vshred.com/privacy-policy'
				: 'https://sculptnation.com/privacy-policy',
		},
		styles: {
			inputText:
				'border-gray-500 border-2 w-full rounded p-2 placeholder:text-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 focus:placeholder:text-blue-500',
			inputError: 'border-red-500 text-red-700 placeholder:text-red-700',
			inputFocus: 'border-blue-500 ring-1 ring-blue-500 ring-opacity-50 placeholder:text-blue-500',
			inputErrorText: 'text-red-700 mb-1 ml-2 text-xs',
		},
		assets: {
			logo_url: vshred
				? 'https://le.vshred.com/cdn-cgi/image/fit=contain/https://assets.sculptnation.com/cms/sales-pages/checkout/secure-checkout.svg'
				: 'https://le.vshred.com/cdn-cgi/image/fit=contain/https://assets.sculptnation.com/cms/sales-pages/checkout/sculpt.svg',
			guarantee_img:
				'https://le.vshred.com/cdn-cgi/image/fit=contain/https://assets.sculptnation.com/cms/sculptnation/img/checkout/moneyback.webp',
			product_banner_img: [
				'https://le.vshred.com/cdn-cgi/image/fit=contain/https://assets.sculptnation.com/cms/sculptnation/img/checkout/flef-checkout-keira-before-after.webp',
				'https://le.vshred.com/cdn-cgi/image/fit=contain/https://assets.sculptnation.com/cms/sculptnation/img/checkout/flef-checkout-zabrina-before-after.webp',
				'https://le.vshred.com/cdn-cgi/image/fit=contain/https://assets.sculptnation.com/cms/sculptnation/img/checkout/flef-checkout-summer-before-after.webp',
			],
			social_proof_img:
				'https://le.vshred.com/cdn-cgi/image/fit=contain/https://assets.sculptnation.com/cms/sculptnation/img/checkout/secret-weapon.webp',
		},
	};
};

const isPlainObject = (value) =>
	value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Recursively merge `override` onto `base`. Plain objects merge key-by-key;
 * everything else (arrays, scalars) replaces wholesale. `undefined` override
 * values are ignored so a partial page option can't blank out a default.
 */
export const deepMerge = (base, override) => {
	if (!isPlainObject(override)) {
		return override === undefined ? base : override;
	}

	const out = isPlainObject(base) ? { ...base } : {};

	for (const [key, value] of Object.entries(override)) {
		if (value === undefined) continue;
		out[key] = isPlainObject(value) && isPlainObject(out[key])
			? deepMerge(out[key], value)
			: value;
	}

	return out;
};

/**
 * Resolve the final skin from baked defaults + page-option overrides + an
 * optional inline `customSkin`. The inline object's legacy per-product `product`
 * array is stripped (per-product hero overrides are no longer supported).
 *
 * @param {{ inlineSkin?: object|null, pageOptions?: object|null }} [args]
 * @returns {object}
 */
export const resolveSkin = ({ inlineSkin = null, pageOptions = null } = {}) => {
	let skin = buildDefaultSkin();

	if (isPlainObject(pageOptions)) {
		skin = deepMerge(skin, pageOptions);
	}

	if (isPlainObject(inlineSkin)) {
		// eslint-disable-next-line no-unused-vars
		const { product, ...rest } = inlineSkin;
		skin = deepMerge(skin, rest);
	}

	return skin;
};
